<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  clearVideoDiagnostics,
  subscribeVideoDiagnostics,
  type VideoDiagnosticEntry,
} from '../utils/videoDiagnostics'

const entries = ref<VideoDiagnosticEntry[]>([])
const isCollapsed = ref(false)
const logList = ref<HTMLElement | null>(null)
let unsubscribe: (() => void) | undefined

const formatTime = (timestamp: number) => {
  const date = new Date(timestamp)
  return [date.getHours(), date.getMinutes(), date.getSeconds()]
    .map((value) => String(value).padStart(2, '0'))
    .join(':')
}

onMounted(() => {
  unsubscribe = subscribeVideoDiagnostics((nextEntries) => {
    entries.value = [...nextEntries]
  })
})

watch(() => entries.value.length, async () => {
  await nextTick()
  if (logList.value) logList.value.scrollTop = logList.value.scrollHeight
})

onBeforeUnmount(() => unsubscribe?.())
</script>

<template>
  <aside class="video-debug" :class="{ 'video-debug--collapsed': isCollapsed }" aria-label="视频播放诊断">
    <header class="video-debug__header">
      <div class="video-debug__title">
        <span class="video-debug__indicator" aria-hidden="true"></span>
        VIDEO DEBUG
        <span class="video-debug__count">{{ entries.length }}</span>
      </div>
      <div class="video-debug__actions">
        <button type="button" @click="clearVideoDiagnostics">清空</button>
        <button type="button" @click="isCollapsed = !isCollapsed">
          {{ isCollapsed ? '展开' : '收起' }}
        </button>
      </div>
    </header>

    <div v-if="!isCollapsed" ref="logList" class="video-debug__logs" aria-live="polite">
      <p v-if="entries.length === 0" class="video-debug__empty">等待 video.play() 结果…</p>
      <article
        v-for="entry in entries"
        :key="entry.id"
        class="video-debug__entry"
        :class="`video-debug__entry--${entry.level}`"
      >
        <div class="video-debug__meta">
          <time>{{ formatTime(entry.timestamp) }}</time>
          <span>{{ entry.trigger }}</span>
          <strong>{{ entry.name }}</strong>
        </div>
        <p>{{ entry.message }}</p>
        <p v-if="entry.diagnosis" class="video-debug__diagnosis">判断：{{ entry.diagnosis }}</p>
      </article>
    </div>
  </aside>
</template>

<style scoped>
.video-debug {
  position: fixed;
  z-index: 10000;
  right: 10px;
  bottom: max(10px, env(safe-area-inset-bottom));
  left: 10px;
  overflow: hidden;
  color: #f5f7fa;
  font: 12px/1.45 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  background: rgba(12, 14, 18, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.35);
}

.video-debug--collapsed {
  left: auto;
  min-width: 245px;
}

.video-debug__header,
.video-debug__title,
.video-debug__actions,
.video-debug__meta {
  display: flex;
  align-items: center;
}

.video-debug__header {
  min-height: 42px;
  padding: 0 8px 0 12px;
  justify-content: space-between;
  gap: 8px;
  background: rgba(255, 255, 255, 0.06);
}

.video-debug__title {
  gap: 7px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.video-debug__indicator {
  width: 8px;
  height: 8px;
  background: #65e687;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(101, 230, 135, 0.8);
}

.video-debug__count {
  min-width: 20px;
  padding: 1px 5px;
  color: #c9d0d9;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.video-debug__actions {
  gap: 4px;
}

.video-debug button {
  min-height: 32px;
  padding: 0 9px;
  color: #e8ebef;
  font: inherit;
  background: transparent;
  border: 0;
  border-radius: 6px;
}

.video-debug button:active {
  background: rgba(255, 255, 255, 0.14);
}

.video-debug__logs {
  max-height: min(42vh, 360px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 6px 10px 10px;
}

.video-debug__empty {
  margin: 10px 2px;
  color: #9ca6b3;
}

.video-debug__entry {
  padding: 7px 2px 8px 9px;
  border-left: 3px solid #8591a0;
}

.video-debug__entry + .video-debug__entry {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.video-debug__entry--success {
  border-left-color: #65e687;
}

.video-debug__entry--error {
  border-left-color: #ff6577;
}

.video-debug__meta {
  flex-wrap: wrap;
  gap: 7px;
  color: #aeb7c2;
}

.video-debug__meta strong {
  color: #fff;
}

.video-debug__entry--success .video-debug__meta strong {
  color: #65e687;
}

.video-debug__entry--error .video-debug__meta strong {
  color: #ff8290;
}

.video-debug__entry p {
  margin: 3px 0 0;
  overflow-wrap: anywhere;
  color: #d9dee5;
}

.video-debug__entry .video-debug__diagnosis {
  color: #ffd76a;
}

@media (min-width: 700px) {
  .video-debug {
    right: 16px;
    bottom: 16px;
    left: auto;
    width: 430px;
  }
}
</style>
